/**
 * Pipeline bootstrap — exposes ESM-side pipeline modules to the legacy
 * non-module editor via window.BlrrdPipeline. Bridge phase only — once
 * blrrd-editor-v15.js itself becomes ESM (Phase 1.B+), this file goes away
 * and consumers import directly.
 *
 * Phase 1.D adds:
 *   • window.BlrrdStorage — persist + estimate helpers
 *   • window.BlrrdIDB     — schema-introspection (consumed by tests today)
 *   • PWA registration    — fired once on import
 */

import { buildPipelineContext } from './build-context.js';
import { applyKernel } from './kernel.js';
import {
    ingestSRGB, ingestSRGBFloat, ingestLinearACEScg, ingestLinearRec709,
    ingestLinearAP0, ingestLinearBT2020, ingestWithMatrix,
} from './ingest.js';
import { parseDCP } from './dcp/loader.js';
import { findDCPForCamera, modelKey } from './dcp/registry.js';
import { buildCameraToRec709 } from './dcp/matrix.js';
import { illuminantToXY, interpWeight } from './dcp/illuminant.js';
import {
    cameraNeutralFromMul,
    cameraToACEScgFallback,
    M_XYZD65_TO_REC709,
} from './dcp/fallback.js';
import { viewTransform } from './view-transform.js';
import {
    buildPreviewSource as buildPreviewSourceESM,
    buildPreviewSourceFromFloat as buildPreviewSourceFromFloatESM,
    LODScheduler,
    PREVIEW_MAX_DIM_DEFAULT,
    FULL_RES_DEBOUNCE_MS_DEFAULT,
    sanitizeRegion,
    unionRegion,
} from './preview-lod.js';
import { detectFormat, decode, FORMATS, UnsupportedFormatError } from './decode.js';
import {
    parseChromaticities as exrParseChromaticities,
    detectColorSpace   as exrDetectColorSpace,
} from './decoders/exr.js';
import {
    recoverHighlights,
    buildClipMask,
    highlightStats,
    DEFAULT_CLIP_THRESHOLD,
    DEFAULT_NEIGHBORHOOD_RADIUS,
    DEFAULT_MAX_RADIUS,
    AUTO_APPLY_THRESHOLD as HIGHLIGHT_AUTO_APPLY_THRESHOLD,
    CLIP_R, CLIP_G, CLIP_B, CLIP_ALL,
} from './highlight-recovery.js';
import * as Batch from './batch.js';
import { openBatchModal } from '../ui/batch-modal.js';
import * as Color from '../color/index.js';
import { HSL_RANGES } from '../color/hsl-ranges.js';
import { initPWA } from '../pwa/register.js';
import { requestPersist, getStorageEstimate } from '../persistence/storage.js';
import { describeDB, openDB, DB_NAME, DB_VERSION, STORES } from '../persistence/idb.js';
import * as Files from '../persistence/files.js';
import * as Projects from '../persistence/projects.js';
import * as Thumbnails from '../persistence/thumbnails.js';
import { attachCanvasGestures } from '../ui/gestures.js';
import { bindAllSliderLongPress, isCoarsePointer } from '../ui/long-press.js';
import { openLibraryModal } from '../ui/library-modal.js';
import { openMerge as openHDRMerge } from '../ui/hdr-merge-modal.js';
import { openMetadataPanel, buildMetadata } from '../ui/metadata-panel.js';
import { openAboutModal, ABOUT_VENDORED_LIBS, ABOUT_BUILD_VERSION } from '../ui/about-modal.js';
import {
    initI18n, t, setLocale, getLocale, getSupportedLocales,
    formatNumber, applyDOM, onLocaleChange,
} from '../i18n/index.js';
import * as HDR from './hdr/index.js';
import { fuseBracketsCPU } from './hdr/index.js';
import {
    captureSharpen, gaussianKernel1D, applyGaussian1D,
} from './capture-sharpen.js';
import {
    suggestAutoTone, luminanceHistogram, percentile,
    sampleMeanChroma, grayWorldWB, isIdentitySuggestion,
} from './auto-tone.js';
import {
    applyToneEqualizer, applyToneEqualizerFromCtx, buildGuideEV,
    defaultSliders as toneEqDefaultSliders,
    isIdentitySliders as toneEqIsIdentitySliders,
    sanitizeSliders as toneEqSanitizeSliders,
    clampSlider as toneEqClampSlider,
    defaultToneEqRadius,
    TONE_EQ_ZONES, TONE_EQ_EV_MIN, TONE_EQ_EV_MAX,
    TONE_EQ_SLIDER_MIN, TONE_EQ_SLIDER_MAX,
    TONE_EQ_DEFAULT_RADIUS, TONE_EQ_DEFAULT_EPS,
} from './tone-equalizer.js';
import * as PointwiseFilters from './filters/pointwise/index.js';
import * as SpatialFilters from './filters/spatial/index.js';

/* Phase 4.E.tools.1 — Advanced tools: eyedropper, bucket fill, gradient.
 * MIT clean-room. ESM-only; consumed by public/blrrd-editor-v15.js via
 * the window.BlrrdTools bridge. */
import {
    samplePixel, createColorHistory, sampleToForeground,
    linRGBtoLab, labToLCh, labToXYZ,
} from '../tools/eyedropper.js';
import { floodFill } from '../tools/bucket-fill.js';
import {
    applyGradient, evalGradient, insertStop, removeStop, DEFAULT_GRADIENT,
} from '../tools/gradient.js';

/* Phase 4.A.layers.1 — pixel-layer system (model, blend modes, compositor,
 * snapshot serialise/deserialise). Exposed on window.BlrrdLayers so the
 * legacy editor (non-ESM) can drive the panel UI + render path. */
import {
    createPixelLayer, isPixelLayer, defaultLayers, isIdentityStack,
    selectPixelLayers, LAYER_KIND_PIXEL,
    DEFAULT_BLEND_MODE, DEFAULT_OPACITY,
    addLayerMask, removeLayerMask, clearLayerMask, invertLayerMask,
} from '../layers/model.js';
import { composite, allocCompositeBuffer } from '../layers/compositor.js';
import {
    BLEND_MODES, BLEND_MODES_NON_SEPARABLE, BLEND_MODE_LIST,
    resolveBlendFn, resolveNonSeparableBlendFn, isNonSeparableBlendMode,
    clampBlendResult,
} from '../layers/blend-modes.js';
import {
    serializeLayers, deserializeLayers, SERIALIZATION_VERSION as LAYERS_SERIALIZATION_VERSION,
} from '../layers/serialize.js';

/* Phase 4.B.select.1 — Selection core (rectangle / ellipse / marching ants).
 * Phase 4.B.select.2 (Wave 8.2) adds Meijster EDT feather, magic-wand,
 * and free-hand lasso. */
import * as SelectionModel from '../selection/model.js';
import * as SelectionCombine from '../selection/combine.js';
import { rasterizeRect } from '../selection/rect.js';
import { rasterizeEllipse } from '../selection/ellipse.js';
import { buildContourPath2D, drawMarchingAnts } from '../selection/overlay.js';
import { featherStub, featherSelection, distanceTransform } from '../selection/feather.js';
import { magicWand, deltaE76 } from '../selection/magic-wand.js';
import { rasterizeLasso, rasterizePoint } from '../selection/lasso.js';

/* Phase 4.G.vector (Wave 12.2) — Path editor (cubic Bezier pen + stroke/
 * fill + path→selection) and Text tool (Canvas-2D fillText → pixel layer).
 * Both MIT clean-room. Path fill reuses the Pineda scanline from the
 * selection lasso module; text bakes into the pixel-layer system. */
import * as PathTool from '../path/index.js';
import * as TextTool from '../text/index.js';

/* Phase 4.D.transform.1 (Wave 11.1) — rotate / scale / flip / free-transform.
 * Standalone module under src/pipeline/transform/. No file collision with
 * the legacy editor's `applyTransform` (CSS canvas pan/zoom); exposed under
 * a fresh `window.BlrrdTransform` namespace below. */
import * as TransformMatrix   from './transform/matrix.js';
import * as TransformResample from './transform/resample.js';
import * as Transform         from './transform/index.js';

/* Phase 4.C.paint.1 (Wave 9.1) — brush / pencil / eraser paint engine.
 * Stamped-brush dab engine (Hughes/van Dam §10.5 + Porter-Duff 1984),
 * cached Gaussian brush masks, spacing-accumulator path walker, and
 * pressure/velocity dynamics. Paints onto the active pixel layer's RGBA
 * buffer (NEVER E.srcFloat). Exposed under `window.BlrrdPaint`. */
import * as Paint from './paint/index.js';

window.BlrrdPipeline = window.BlrrdPipeline || {};
window.BlrrdPipeline.buildPipelineContext = buildPipelineContext;
window.BlrrdPipeline.applyKernel          = applyKernel;
window.BlrrdPipeline.ingestSRGB           = ingestSRGB;
window.BlrrdPipeline.ingestSRGBFloat      = ingestSRGBFloat;
window.BlrrdPipeline.ingestLinearACEScg   = ingestLinearACEScg;
window.BlrrdPipeline.ingestLinearRec709   = ingestLinearRec709;
/* Phase 3.C.1 — chromaticities-aware EXR dispatch. Most callers won't
 * hit these directly because the EXR decoder applies the matrix in-place
 * and tags the result `aces-ap1`, but we expose them so future formats
 * (TIFF with primaries tag, DPX with colour-space header) can route
 * through the same shape as ingestLinearRec709. */
window.BlrrdPipeline.ingestLinearAP0      = ingestLinearAP0;
window.BlrrdPipeline.ingestLinearBT2020   = ingestLinearBT2020;
window.BlrrdPipeline.ingestWithMatrix     = ingestWithMatrix;   /* Phase 3.B-color P4 — DCP wire-through */
window.BlrrdPipeline.viewTransform        = viewTransform;

/* Phase 3.B-color P6 — opposed-colour highlight recovery. One-shot,
 * post-ingest, pre-kernel. RAW decoder auto-invokes when > 5% of pixels
 * are clipped; the helpers are also exposed here so the UI can offer
 * a manual "Recover highlights" button + a "X% clipped" badge. */
window.BlrrdPipeline.recoverHighlights              = recoverHighlights;
window.BlrrdPipeline.buildClipMask                  = buildClipMask;
window.BlrrdPipeline.highlightStats                 = highlightStats;
window.BlrrdPipeline.HIGHLIGHT_DEFAULT_THRESHOLD    = DEFAULT_CLIP_THRESHOLD;
window.BlrrdPipeline.HIGHLIGHT_DEFAULT_RADIUS       = DEFAULT_NEIGHBORHOOD_RADIUS;
window.BlrrdPipeline.HIGHLIGHT_MAX_RADIUS           = DEFAULT_MAX_RADIUS;
window.BlrrdPipeline.HIGHLIGHT_AUTO_APPLY_THRESHOLD = HIGHLIGHT_AUTO_APPLY_THRESHOLD;
window.BlrrdPipeline.HIGHLIGHT_FLAGS = {
    R:   CLIP_R,
    G:   CLIP_G,
    B:   CLIP_B,
    ALL: CLIP_ALL,
};

/* Phase 3.B-detail P8 — capture sharpening (Richardson-Lucy
 * deconvolution). Exposed so the test gate can probe the standalone
 * helper + the integration via the RAW decoder. */
window.BlrrdPipeline.captureSharpen     = captureSharpen;
window.BlrrdPipeline.gaussianKernel1D   = gaussianKernel1D;
window.BlrrdPipeline.applyGaussian1D    = applyGaussian1D;

/* Phase 4-bonus — Auto-Tone histogram analysis. Suggests exposure /
 * contrast / saturation / WB adjustments by reading the luminance
 * histogram + sampled chroma. Clean-room from Reinhard 2002 (mid-grey
 * target) + Buchsbaum 1980 (gray-world WB). Used by the legacy editor's
 * Auto-Tone toolbar action (keyboard `A`) and exposed for power users. */
window.BlrrdAutoTone = window.BlrrdAutoTone || {};
window.BlrrdAutoTone.suggest             = suggestAutoTone;
window.BlrrdAutoTone.luminanceHistogram  = luminanceHistogram;
window.BlrrdAutoTone.percentile          = percentile;
window.BlrrdAutoTone.sampleMeanChroma    = sampleMeanChroma;
window.BlrrdAutoTone.grayWorldWB         = grayWorldWB;
window.BlrrdAutoTone.isIdentity          = isIdentitySuggestion;

/* Phase 3.B-tone P7 — Tone Equalizer (9-zone luminance bands + EIGF).
 * The renderer + worker call `applyToneEqualizerFromCtx` as a pre-kernel
 * pass; the standalone `applyToneEqualizer` + `buildGuideEV` helpers
 * are exposed for the test gate. UI panel writes E.toneEqualizer.sliders. */
window.BlrrdPipeline.applyToneEqualizer        = applyToneEqualizer;
window.BlrrdPipeline.applyToneEqualizerFromCtx = applyToneEqualizerFromCtx;
window.BlrrdPipeline.buildGuideEV              = buildGuideEV;
window.BlrrdPipeline.toneEqDefaultSliders      = toneEqDefaultSliders;
window.BlrrdPipeline.toneEqIsIdentitySliders   = toneEqIsIdentitySliders;
window.BlrrdPipeline.toneEqSanitizeSliders     = toneEqSanitizeSliders;
window.BlrrdPipeline.toneEqClampSlider         = toneEqClampSlider;
window.BlrrdPipeline.defaultToneEqRadius       = defaultToneEqRadius;
window.BlrrdPipeline.TONE_EQ_ZONES             = TONE_EQ_ZONES;
window.BlrrdPipeline.TONE_EQ_EV_MIN            = TONE_EQ_EV_MIN;
window.BlrrdPipeline.TONE_EQ_EV_MAX            = TONE_EQ_EV_MAX;
window.BlrrdPipeline.TONE_EQ_SLIDER_MIN        = TONE_EQ_SLIDER_MIN;
window.BlrrdPipeline.TONE_EQ_SLIDER_MAX        = TONE_EQ_SLIDER_MAX;
window.BlrrdPipeline.TONE_EQ_DEFAULT_RADIUS    = TONE_EQ_DEFAULT_RADIUS;
window.BlrrdPipeline.TONE_EQ_DEFAULT_EPS       = TONE_EQ_DEFAULT_EPS;

/* Phase 4.E.filters.1 — Pointwise filters (Wave 11.2).
 * Nine display-/scene-referred per-pixel filters: Levels, Curves-multi,
 * Threshold, Posterize, Solarize, Sepia, Channel Mixer, Color Balance,
 * Gradient Map. All identity-on-defaults (preserves pixel-hash + worker-
 * parity baselines byte-identical when toggled but unconfigured). See
 * docs/algorithms/filters-pointwise.md for citations + slot table. */
window.BlrrdFilters = window.BlrrdFilters || {};
window.BlrrdFilters.pointwise = PointwiseFilters;

/* Phase 4.E.filters.2 — spatial filters (Wave 11.3).
 *
 * Nine destructive-commit filters that operate pre-kernel on scene-
 * linear ACEScg Float32 RGBA. BlrrdFilters is a shared namespace —
 * `.pointwise` and `.spatial` are sibling sub-keys.
 */
window.BlrrdFilters.spatial = SpatialFilters;

/* Phase 4.A.layers.1 — pixel-layer compositor namespace. The legacy
 * editor calls these from its render loop + Layers panel UI. The math
 * lives entirely in src/layers/* (W3C Compositing-and-Blending Level 1
 * + Porter-Duff 1984 SIGGRAPH). Identity short-circuit guarantees
 * byte-identical pixel-hash / worker-parity baselines when no pixel
 * layers exist. */
window.BlrrdLayers = window.BlrrdLayers || {};
window.BlrrdLayers.createPixelLayer    = createPixelLayer;
window.BlrrdLayers.isPixelLayer        = isPixelLayer;
window.BlrrdLayers.defaultLayers       = defaultLayers;
window.BlrrdLayers.isIdentityStack     = isIdentityStack;
window.BlrrdLayers.selectPixelLayers   = selectPixelLayers;
window.BlrrdLayers.composite           = composite;
window.BlrrdLayers.allocCompositeBuffer = allocCompositeBuffer;
window.BlrrdLayers.BLEND_MODES               = BLEND_MODES;
window.BlrrdLayers.BLEND_MODES_NON_SEPARABLE = BLEND_MODES_NON_SEPARABLE;
window.BlrrdLayers.BLEND_MODE_LIST           = BLEND_MODE_LIST;
window.BlrrdLayers.resolveBlendFn            = resolveBlendFn;
window.BlrrdLayers.resolveNonSeparableBlendFn = resolveNonSeparableBlendFn;
window.BlrrdLayers.isNonSeparableBlendMode   = isNonSeparableBlendMode;
window.BlrrdLayers.clampBlendResult          = clampBlendResult;
window.BlrrdLayers.serializeLayers           = serializeLayers;
window.BlrrdLayers.deserializeLayers         = deserializeLayers;
/* Phase 4.A.layers.2 — mask API (add/remove/clear/invert). All
 * immutable: each returns a new layer object; callers replace the
 * stack entry rather than mutating in place. */
window.BlrrdLayers.addLayerMask        = addLayerMask;
window.BlrrdLayers.removeLayerMask     = removeLayerMask;
window.BlrrdLayers.clearLayerMask      = clearLayerMask;
window.BlrrdLayers.invertLayerMask     = invertLayerMask;
window.BlrrdLayers.LAYER_KIND_PIXEL    = LAYER_KIND_PIXEL;
window.BlrrdLayers.DEFAULT_BLEND_MODE  = DEFAULT_BLEND_MODE;
window.BlrrdLayers.DEFAULT_OPACITY     = DEFAULT_OPACITY;
window.BlrrdLayers.SERIALIZATION_VERSION = LAYERS_SERIALIZATION_VERSION;

/* Phase 4.B.select.1 — Selection core surface. Bound under
 * `window.BlrrdSelect` to keep the namespace separate from the rest of
 * the pipeline (selection isn't strictly part of the per-pixel kernel —
 * it's an orthogonal coordinate system that the kernel CONSUMES via
 * the build-context). */
window.BlrrdSelect = window.BlrrdSelect || {};
window.BlrrdSelect.createSelection      = SelectionModel.createSelection;
window.BlrrdSelect.isAllSelected        = SelectionModel.isAllSelected;
window.BlrrdSelect.ensureMask           = SelectionModel.ensureMask;
window.BlrrdSelect.clearSelection       = SelectionModel.clearSelection;
window.BlrrdSelect.invertSelection      = SelectionModel.invertSelection;
window.BlrrdSelect.bumpVersion          = SelectionModel.bumpVersion;
window.BlrrdSelect.serializeSelection   = SelectionModel.serializeSelection;
window.BlrrdSelect.deserializeSelection = SelectionModel.deserializeSelection;
window.BlrrdSelect.combine              = SelectionCombine.combine;
window.BlrrdSelect.opFromModifiers      = SelectionCombine.opFromModifiers;
window.BlrrdSelect.COMBINE_OPS          = SelectionCombine.COMBINE_OPS;
window.BlrrdSelect.rasterizeRect        = rasterizeRect;
window.BlrrdSelect.rasterizeEllipse     = rasterizeEllipse;
window.BlrrdSelect.buildContourPath2D   = buildContourPath2D;
window.BlrrdSelect.drawMarchingAnts     = drawMarchingAnts;
window.BlrrdSelect.featherStub          = featherStub;        /* back-compat alias */
window.BlrrdSelect.featherSelection     = featherSelection;   /* Wave 8.2 — Meijster EDT */
window.BlrrdSelect.distanceTransform    = distanceTransform;
window.BlrrdSelect.magicWand            = magicWand;
window.BlrrdSelect.deltaE76             = deltaE76;
window.BlrrdSelect.rasterizeLasso       = rasterizeLasso;
window.BlrrdSelect.rasterizePoint       = rasterizePoint;

/* Phase 4.D.transform.1 (Wave 11.1) — Transform pipeline.
 * Exposes 3×3-matrix + resampler + applyTransform on a fresh
 * `window.BlrrdTransform` namespace. Does NOT collide with the legacy
 * editor's `window.applyTransform` (which is CSS canvas pan/zoom for
 * the wrapper element). */
window.BlrrdTransform = window.BlrrdTransform || {};
Object.assign(window.BlrrdTransform, Transform);
window.BlrrdTransform.Matrix     = TransformMatrix;
window.BlrrdTransform.Resample   = TransformResample;
window.BlrrdTransform.RESAMPLERS = TransformResample.RESAMPLERS;

/* Phase 4.C.paint.1 (Wave 9.1) — paint engine bridge. The legacy editor's
 * initPaintTools() drives strokes via these primitives: getBrushMask →
 * startStroke → strokeTo(stamp) → finishStroke, with dispatchDab routing
 * to the brush / pencil / eraser ops. Pure functions, no DOM, no side
 * effects on import — importing this does NOT touch any render buffer, so
 * the pixel-hash + worker-parity baselines stay byte-identical until a
 * dab is explicitly stamped during an active stroke. */
window.BlrrdPaint = window.BlrrdPaint || {};
Object.assign(window.BlrrdPaint, Paint);

/* Phase 3.B-color P4 — DCP camera-profile API surface, exposed for
 * the test gate + future preference-UI hooks. RAW decoder calls these
 * internally already. */
window.BlrrdDCP = window.BlrrdDCP || {};
window.BlrrdDCP.parseDCP                = parseDCP;
window.BlrrdDCP.findDCPForCamera        = findDCPForCamera;
window.BlrrdDCP.modelKey                = modelKey;
window.BlrrdDCP.buildCameraToRec709     = buildCameraToRec709;
/* Phase 3.B.1 A1 — DCP wire-through helpers. cameraNeutralFromMul
 * derives scene xy chromaticity from libraw's cam_mul/pre_mul WB
 * multipliers; cameraToACEScgFallback composes a single 3×3 from
 * libraw's rgb_cam (currently dormant — libraw-wasm 1.1.2 doesn't
 * surface rgb_cam, see fallback.js notes). illuminantToXY +
 * interpWeight let callers reproduce the ColorMatrix1↔2 blend. */
window.BlrrdDCP.cameraNeutralFromMul    = cameraNeutralFromMul;
window.BlrrdDCP.cameraToACEScgFallback  = cameraToACEScgFallback;
window.BlrrdDCP.illuminantToXY          = illuminantToXY;
window.BlrrdDCP.interpWeight            = interpWeight;
window.BlrrdDCP.M_XYZD65_TO_REC709      = M_XYZD65_TO_REC709;

/* Phase 3.B-perf P5 — LOD preview pipeline. The legacy editor consumes
 * `buildPreviewSource` to construct the 1440-px preview cache once per
 * image load, and instantiates an `LODScheduler` to coalesce slider
 * events into preview/full tiers. See src/pipeline/preview-lod.js for
 * the full contract. */
window.BlrrdPipeline.buildPreviewSource         = buildPreviewSourceESM;
window.BlrrdPipeline.buildPreviewSourceFromFloat = buildPreviewSourceFromFloatESM;
window.BlrrdPipeline.LODScheduler               = LODScheduler;
window.BlrrdPipeline.PREVIEW_MAX_DIM_DEFAULT    = PREVIEW_MAX_DIM_DEFAULT;
window.BlrrdPipeline.FULL_RES_DEBOUNCE_MS_DEFAULT = FULL_RES_DEBOUNCE_MS_DEFAULT;
/* Phase 4.F.polish.1 / Wave 12.2 — region-invalidation helpers exposed for
 * test gate + future paint-stroke call sites (Wave 9). Pure, side-effect-
 * free utilities — safe to expose on the global bridge. */
window.BlrrdPipeline.sanitizeRegion             = sanitizeRegion;
window.BlrrdPipeline.unionRegion                = unionRegion;

/* Phase 3.A.0 — format dispatcher. Magic-bytes detect + lazy-loaded
 * decoder registry. PNG/JPEG/WebP/AVIF/GIF/BMP go through native
 * `<img>`; HEIC/TIFF/EXR/DPX/RAW are recognised but throw
 * UnsupportedFormatError until their per-phase decoder lands. */
window.BlrrdDecode = window.BlrrdDecode || {};
window.BlrrdDecode.detectFormat            = detectFormat;
window.BlrrdDecode.decode                  = decode;
window.BlrrdDecode.FORMATS                 = FORMATS;
window.BlrrdDecode.UnsupportedFormatError  = UnsupportedFormatError;
/* Phase 3.C.1 — chromaticities helpers exposed for diagnostics + test
 * gates. Pure JS (no WASM dependency) so they're safe to bind eagerly
 * even though the WASM-using `decode` is still lazy-loaded via the
 * DECODERS registry. */
window.BlrrdDecode.exr = window.BlrrdDecode.exr || {};
window.BlrrdDecode.exr.parseChromaticities = exrParseChromaticities;
window.BlrrdDecode.exr.detectColorSpace    = exrDetectColorSpace;

/* Phase 3.A.batch — multi-file format conversion with cancellation
 * handle. v1 = format conversion only (no preset application; built-in
 * presets use post-effects that aren't ESM-extracted yet — Phase v1.5
 * follow-up). Lazy-loads JSZip for the ZIP-download save mode. */
window.BlrrdBatch = window.BlrrdBatch || {};
window.BlrrdBatch.start                    = Batch.start;
window.BlrrdBatch.runZip                   = Batch.runZip;
window.BlrrdBatch.openBatchModal           = openBatchModal;
window.BlrrdBatch.MAX_INPUT_PIXELS         = Batch.MAX_INPUT_PIXELS;
window.BlrrdBatch.MAX_BATCH_OUTPUT_BYTES   = Batch.MAX_BATCH_OUTPUT_BYTES;
window.BlrrdBatch.SAFETY_DIM_CAP           = Batch.SAFETY_DIM_CAP;

window.BlrrdColor = window.BlrrdColor || {};
Object.assign(window.BlrrdColor, Color);
window.BlrrdColor.HSL_RANGES = HSL_RANGES;

/* Phase 4.E.tools.1 — window.BlrrdTools bridge. Three tool modules
 * exposed for the legacy editor to consume on cursor/click events.
 * All three operate in scene-linear ACEScg on Float32 RGBA buffers
 * (E.srcFloat). Display-space conversion is the renderer's job. */
window.BlrrdTools = window.BlrrdTools || {};
window.BlrrdTools.samplePixel         = samplePixel;
window.BlrrdTools.createColorHistory  = createColorHistory;
window.BlrrdTools.sampleToForeground  = sampleToForeground;
window.BlrrdTools.linRGBtoLab         = linRGBtoLab;
window.BlrrdTools.labToLCh            = labToLCh;
window.BlrrdTools.labToXYZ            = labToXYZ;
window.BlrrdTools.floodFill           = floodFill;
window.BlrrdTools.applyGradient       = applyGradient;
window.BlrrdTools.evalGradient        = evalGradient;
window.BlrrdTools.insertStop          = insertStop;
window.BlrrdTools.removeStop          = removeStop;
window.BlrrdTools.DEFAULT_GRADIENT    = DEFAULT_GRADIENT;

/* Phase 4.G.vector (Wave 12.2) — Path editor surface. Cubic-Bezier model
 * + de-Casteljau eval + adaptive flatten + scanline stroke/fill + path→
 * selection mask. The legacy editor drives the pen-tool UI through this
 * bridge; the pure-Node gate imports src/path/index.js directly. */
window.BlrrdPath = window.BlrrdPath || {};
Object.assign(window.BlrrdPath, PathTool);

/* Phase 4.G.vector (Wave 12.2) — Text tool surface. Canvas-2D fillText
 * layout + bake into an ACEScg pixel-layer buffer. System fonts only
 * (privacy-first — no web fonts). */
window.BlrrdText = window.BlrrdText || {};
Object.assign(window.BlrrdText, TextTool);

window.BlrrdStorage = window.BlrrdStorage || {};
window.BlrrdStorage.requestPersist     = requestPersist;
window.BlrrdStorage.getStorageEstimate = getStorageEstimate;

window.BlrrdIDB = window.BlrrdIDB || {};
window.BlrrdIDB.describeDB = describeDB;
window.BlrrdIDB.openDB     = openDB;
window.BlrrdIDB.NAME       = DB_NAME;
window.BlrrdIDB.VERSION    = DB_VERSION;
window.BlrrdIDB.STORES     = STORES;

window.BlrrdFiles      = window.BlrrdFiles      || {};
window.BlrrdProjects   = window.BlrrdProjects   || {};
window.BlrrdThumbnails = window.BlrrdThumbnails || {};
Object.assign(window.BlrrdFiles,      Files);
Object.assign(window.BlrrdProjects,   Projects);
Object.assign(window.BlrrdThumbnails, Thumbnails);

window.BlrrdUI = window.BlrrdUI || {};
window.BlrrdUI.attachCanvasGestures   = attachCanvasGestures;
window.BlrrdUI.bindAllSliderLongPress = bindAllSliderLongPress;
window.BlrrdUI.isCoarsePointer        = isCoarsePointer;
window.BlrrdUI.openLibraryModal       = openLibraryModal;
/* Phase 3.B-ux P9: metadata panel — read-only EXIF / RAW / AI viewer.
 * Mounts a lazy <dialog> on first call. Reads from window.E (legacy
 * editor's central state) for current image context. */
window.BlrrdUI.openMetadataPanel      = openMetadataPanel;
window.BlrrdUI.buildMetadata          = buildMetadata;
/* Phase 1.H — About / launch surface. Surfaces v1.0 license attribution
 * (LGPL-3.0 libheif, LGPL-2.1 libraw, BSD-3 exrs, MIT JSZip / UTIF /
 * piexifjs / shallow-equals) per LGPL combined-work obligations.
 * Lazily mounts the modal on first call. */
window.BlrrdUI.openAboutModal         = openAboutModal;
window.BlrrdUI.ABOUT_VENDORED_LIBS    = ABOUT_VENDORED_LIBS;
window.BlrrdUI.ABOUT_BUILD_VERSION    = ABOUT_BUILD_VERSION;

window.BlrrdI18n = window.BlrrdI18n || {};
window.BlrrdI18n.t                   = t;
window.BlrrdI18n.setLocale           = setLocale;
window.BlrrdI18n.getLocale           = getLocale;
window.BlrrdI18n.getSupportedLocales = getSupportedLocales;
window.BlrrdI18n.formatNumber        = formatNumber;
window.BlrrdI18n.applyDOM            = applyDOM;
window.BlrrdI18n.onLocaleChange      = onLocaleChange;

/* ── HDR Pipeline (Phase 2A) ──────────────────────────────────────────────
 * Test scripts (test-hdr-fuse, test-hdr-gpu-parity, test-hdr-memory) probe
 * window.BlrrdHDR first and fall back to dynamic import. Mirror the ESM
 * surface here so the GPU parity gate finds fuseBracketsGPU + the boolean
 * gpuAvailable snapshot. Probing GPU at module-load time is acceptable — it
 * only constructs an offscreen 2×2 canvas and compiles shaders, ~5 ms even
 * on integrated GPUs. */
window.BlrrdHDR = window.BlrrdHDR || {};
window.BlrrdHDR.fuseBrackets       = HDR.fuseBrackets;
window.BlrrdHDR.fuseBracketsCPU    = fuseBracketsCPU;
window.BlrrdHDR.fuseBracketsGPU    = HDR.fuseBracketsGPU;
window.BlrrdHDR.fuseBracketsAuto   = HDR.fuseBracketsAuto;
window.BlrrdHDR.selectBackend      = HDR.selectBackend;
window.BlrrdHDR.disposeBackend     = HDR.disposeBackend;
window.BlrrdHDR.WebGL2Backend      = HDR.WebGL2Backend;
window.BlrrdHDR.computeWeights     = HDR.computeWeights;
window.BlrrdHDR.normalizeWeights   = HDR.normalizeWeights;
window.BlrrdHDR.gaussianPyramid    = HDR.gaussianPyramid;
window.BlrrdHDR.laplacianPyramid   = HDR.laplacianPyramid;
window.BlrrdHDR.collapsePyramid    = HDR.collapsePyramid;
/* Phase 1.H — contrastMode helpers (test surface). */
window.BlrrdHDR.buildLaplacianWeight = HDR.buildLaplacianWeight;
window.BlrrdHDR.buildStdevWeight     = HDR.buildStdevWeight;
/* Codex R1 follow-up: guided-filter helpers are needed by the HDR test
 * suite for the dominance gate. Keep the bootstrap surface symmetric
 * with src/pipeline/hdr/index.js exports so the tests don't need a
 * second dynamic-import fallback. */
window.BlrrdHDR.guidedFilter        = HDR.guidedFilter;
window.BlrrdHDR.boxFilter           = HDR.boxFilter;
window.BlrrdHDR.luminanceFromRGBA   = HDR.luminanceFromRGBA;
window.BlrrdHDR.selectGuideIndex    = HDR.selectGuideIndex;
window.BlrrdHDR.defaultGuidedRadius = HDR.defaultGuidedRadius;
window.BlrrdHDR.DEFAULT_GUIDED_EPS  = HDR.DEFAULT_GUIDED_EPS;
window.BlrrdHDR.DEFAULT_MU_EV       = HDR.DEFAULT_MU_EV;
window.BlrrdHDR.DEFAULT_SIGMA_EV    = HDR.DEFAULT_SIGMA_EV;
window.BlrrdHDR.DEFAULT_EV_FLOOR    = HDR.DEFAULT_EV_FLOOR;
/* Phase 2.A.2 — AI-Robust HDR Fusion helpers. detectFusionMode is needed
 * by both the modal flow (window) and the test suite. */
window.BlrrdHDR.pixelMedianMaps     = HDR.pixelMedianMaps;
window.BlrrdHDR.pixelMedianRGBA     = HDR.pixelMedianRGBA;
window.BlrrdHDR.hybridMix           = HDR.hybridMix;
window.BlrrdHDR.mixMapFromWeights   = HDR.mixMapFromWeights;
window.BlrrdHDR.applyHybridBlendInPlace = HDR.applyHybridBlendInPlace;
window.BlrrdHDR.detectFusionMode    = HDR.detectFusionMode;
window.BlrrdHDR.AUTO_DETECT_THRESHOLDS = HDR.AUTO_DETECT_THRESHOLDS;
window.BlrrdHDR.mergeBrackets      = HDR.mergeBrackets;
window.BlrrdHDR.estimateEVStops    = HDR.estimateEVStops;
window.BlrrdHDR.parseExifEV        = HDR.parseExifEV;
window.BlrrdHDR.reinhard           = HDR.reinhard;
window.BlrrdHDR.hable              = HDR.hable;
window.BlrrdHDR.acesFitted         = HDR.acesFitted;
window.BlrrdHDR.synthesizeBrackets = HDR.synthesizeBrackets;
window.BlrrdHDR.applyEV            = HDR.applyEV;
window.BlrrdHDR.rawToHDR           = HDR.rawToHDR;
window.BlrrdHDR.decodeRaw          = HDR.decodeRaw;
/* Boolean snapshot — evaluated at module load, NOT a getter. The brief
 * specifies "evaluated at module load time" so a single probe runs once
 * and the result is cached on the namespace. Lane C reads this for the
 * "Backend: WebGL2 RGBA32F" UI badge. */
try {
    window.BlrrdHDR.gpuAvailable = HDR.gpuAvailable();
} catch (_) {
    window.BlrrdHDR.gpuAvailable = false;
}
/* ── AI metadata (Phase 2.B.1) ──────────────────────────────────────── */
import * as PNGMeta from './png-metadata.js';
import { extractAIMetadata } from './ai-metadata-extract.js';
import { mountAIPanel, renderAIMetadata } from '../ui/ai-metadata-panel.js';

/* Mount the AI panel when DOM is ready. Idempotent — second call no-ops. */
function maybeMountAIPanel() {
    if (typeof document === 'undefined') return;
    const mount = document.getElementById('aiMetadataPanelMount');
    const tab   = document.getElementById('tabAi');
    if (mount) mountAIPanel(mount, tab);
}
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', maybeMountAIPanel, { once: true });
    } else {
        maybeMountAIPanel();
    }
}

window.BlrrdAI = window.BlrrdAI || {};
/* Read raw chunks + normalized struct from a Blob in one async call.
 * Returns { metadata, aiChunks } or null on parse failure. */
window.BlrrdAI.scanBlobForMetadata = async function (blob) {
    if (!blob || !(blob instanceof Blob)) return null;
    let bytes;
    try {
        const buf = await blob.arrayBuffer();
        bytes = new Uint8Array(buf);
    } catch { return null; }
    if (!PNGMeta.isPNG(bytes)) return null;
    let chunks;
    try { chunks = PNGMeta.listChunks(bytes); }
    catch { return null; }
    const aiChunks = PNGMeta.filterAIChunks(chunks).map(c => ({
        type: c.type, keyword: c.keyword,
        /* Copy the raw bytes — the original Blob's underlying buffer is
         * about to be GC'd when the caller drops the Blob reference, so
         * we need our own slice for export-time splice. */
        raw: c.raw.slice(),
    }));
    const metadata = extractAIMetadata(bytes);
    return { metadata, aiChunks };
};

/* Splice saved AI chunks into a fresh PNG dataURL (output of canvas
 * toDataURL). Returns a new dataURL with chunks before IEND. */
window.BlrrdAI.spliceMetadataIntoDataURL = async function (dataURL, aiChunks) {
    if (!dataURL || !aiChunks || aiChunks.length === 0) return dataURL;
    /* dataURL → bytes. */
    const comma = dataURL.indexOf(',');
    if (comma < 0) return null;
    const b64 = dataURL.slice(comma + 1);
    let bin;
    try { bin = atob(b64); }
    catch { return null; }
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i) & 0xFF;
    let spliced;
    try { spliced = PNGMeta.splicePNGChunks(bytes, aiChunks); }
    catch { return null; }
    /* bytes → dataURL. */
    let out = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < spliced.length; i += CHUNK) {
        out += String.fromCharCode.apply(null, spliced.subarray(i, i + CHUNK));
    }
    return 'data:image/png;base64,' + btoa(out);
};

window.BlrrdAI.renderPanel        = renderAIMetadata;
window.BlrrdAI.extractAIMetadata  = extractAIMetadata;
window.BlrrdAI.PNGMeta             = PNGMeta;

/* Phase 2.B.3 — AI-tell metrics. */
import { computeAITellScores } from './ai-tell-metrics.js';
window.BlrrdAI.computeTellScores = computeAITellScores;

/* Phase 2.B.2 — diffusion color-cast correction. */
import { matchCast as castMatcher, castToLook, getCastById, listCasts } from './diffusion-cast.js';
window.BlrrdAI.matchCast    = castMatcher;
window.BlrrdAI.castToLook   = castToLook;
window.BlrrdAI.getCastById  = getCastById;
window.BlrrdAI.listCasts    = listCasts;
/* applyCast: route a cast through the legacy editor's applyLook(). The
 * legacy editor exposes applyLook on window via blrrd-editor-v15.js
 * load, so we look it up at click time (not at module-load time —
 * that would race the legacy script). */
window.BlrrdAI.applyCast = function (cast) {
    if (!cast) return;
    const look = castToLook(cast);
    if (!look) return;
    if (typeof window.applyLook === 'function') {
        window.applyLook(look);
    }
};

/* `openMerge` is Lane C's UI entry — exposes the native <dialog> mounted
 * lazily by src/ui/hdr-merge-modal.js. Drag-drop in the legacy editor and
 * the toolbar HDR button both dispatch through this seam. */
window.BlrrdHDR.openMerge = openHDRMerge;

/* Resolve locale (localStorage > navigator.language > 'de') and walk the
 * static document once. Runs synchronously — JSON dictionaries are bundled
 * via static imports above so no fetch is required. */
initI18n();

/* Hook the toolbar DE/EN switcher. The button's own label is data-i18n="lang"
 * — applyDOM auto-flips it when setLocale fires. Click toggles between the
 * two supported locales (de ↔ en); future locales would warrant a select. */
function wireLocaleSwitcher() {
    const btn = document.getElementById('btnLocale');
    if (!btn || btn.__blrrdWired) return;
    btn.__blrrdWired = true;
    btn.addEventListener('click', () => {
        const next = getLocale() === 'de' ? 'en' : 'de';
        setLocale(next);
    });
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireLocaleSwitcher);
} else {
    wireLocaleSwitcher();
}

/* PWA registration is fire-and-forget. If the SW isn't eligible (e.g.
 * file:// preview, http on a real host) initPWA returns null silently. */
initPWA();

/* Wire touch gestures + long-press once the legacy editor has booted. We
 * can't reliably hook DOMContentLoaded — top-level `const` bindings in
 * classic scripts aren't on `window`, so they're only reachable after the
 * legacy init explicitly aliases them. The bridge is a one-shot callback
 * the legacy script invokes at the end of its DOMContentLoaded handler. */
function wireTouchUX() {
    if (window.__blrrdTouchWired) return;
    const area = document.getElementById('canvasArea');
    if (!area || !window.E || typeof window.applyTransform !== 'function') return;
    window.__blrrdTouchWired = true;
    attachCanvasGestures(area, window.E, () => window.applyTransform());
    bindAllSliderLongPress();
}
window.__blrrdNotifyReady = wireTouchUX;

/* Belt-and-braces: if the legacy script doesn't call us (older builds or
 * skipped init), still try once after window.load. */
window.addEventListener('load', () => { setTimeout(wireTouchUX, 0); });
